import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1719329466287-047a981ce666?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHBva2UlMjBib3dsJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwY2hlZnxlbnwwfHx8fDE3NzI0OTM1NTZ8MA&ixlib=rb-4.1.0&q=85';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [bgImage, setBgImage] = useState(DEFAULT_IMAGE);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, initAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) { navigate('/'); return; }
    const init = async () => {
      try {
        await initAdmin();
        // Load custom login image
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/login-image`);
        const data = await res.json();
        if (data.url) setBgImage(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      } catch (e) { console.error('Init error:', e); }
      finally { setInitializing(false); }
    };
    init();
  }, [initAdmin, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (userData?.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true); setForgotMsg('');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotMsg('Si un compte existe pour cet email, un mot de passe temporaire vient d\'etre envoye. Verifiez votre boite mail (et les spams).');
    } catch {
      setForgotMsg('Une erreur est survenue. Reessayez.');
    } finally { setForgotLoading(false); }
  };

  if (initializing) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="font-heading text-4xl font-bold mb-4">Chana Poke Thai</h1>
          <p className="text-lg text-white/80 max-w-md">
            Gerez vos KPIs, suivez vos revenus et optimisez vos performances avec notre tableau de bord complet.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-bold text-xl">CP</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">Chana Poke Thai</h1>
              <p className="text-sm text-muted-foreground">KPI Dashboard</p>
            </div>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading">Connexion</CardTitle>
              <CardDescription>Entrez vos identifiants pour acceder au tableau de bord</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="votre@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input id="password" type="password" placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connexion...</> : 'Se connecter'}
                </Button>
              </form>
              <div className="mt-4 text-center">
                {!forgotOpen ? (
                  <button type="button" onClick={() => { setForgotOpen(true); setForgotMsg(''); }}
                    className="text-sm text-primary hover:underline" data-testid="forgot-link">
                    Mot de passe oublie ?
                  </button>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-2 text-left border-t pt-4">
                    <Label htmlFor="forgot-email" className="text-sm">Recevoir un mot de passe temporaire par email</Label>
                    <Input id="forgot-email" type="email" placeholder="votre@email.com" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email" />
                    {forgotMsg && <p className="text-xs text-muted-foreground">{forgotMsg}</p>}
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={forgotLoading} data-testid="forgot-submit">
                        {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setForgotOpen(false)}>Annuler</Button>
                    </div>
                  </form>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
